import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  X, Loader2, Upload, Search, Plus, ChevronRight, ChevronDown,
  Calendar, Filter, Download, Check, AlertTriangle, FileSpreadsheet,
  Clock, Edit3, RefreshCw, Trash2,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { toast } from "sonner";
import { API } from "./apiConfig";

const STATUS_STYLES = {
  New: "bg-green-50 text-green-800 border-green-200",
  Updated: "bg-amber-50 text-amber-800 border-amber-200",
  Dropped: "bg-red-50 text-red-400",
  Active: "",
  Completed: "bg-slate-50 text-slate-500",
};

const STATUS_BADGE = {
  New: "bg-green-100 text-green-800",
  Updated: "bg-amber-100 text-amber-800",
  Dropped: "bg-red-100 text-red-800",
  Active: "bg-blue-100 text-blue-800",
  Completed: "bg-slate-100 text-slate-600",
};

export function WeddingTracker({ isOpen, onClose }) {
  const [tab, setTab] = useState("import");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-0 sm:p-2" data-testid="wedding-tracker">
      <div className="bg-slate-50 rounded-none sm:rounded-xl w-full h-full sm:h-auto sm:max-w-7xl sm:max-h-[95vh] overflow-hidden flex flex-col shadow-xl">
        <div className="bg-[#0A1F3B] px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileSpreadsheet size={18} className="text-[#F5D300]" />
            <h2 className="text-white font-semibold text-base">Wedding Tracker</h2>
          </div>
          <div className="flex items-center gap-2">
            {[["import", "Import Center"], ["master", "Master List"]].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)}
                className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${tab === key ? "bg-[#F5D300] text-black" : "text-white/60 hover:text-white"}`}
                data-testid={`wt-tab-${key}`}>
                {label}
              </button>
            ))}
            <button onClick={onClose} className="text-white/60 hover:text-white ml-4" data-testid="wt-close">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {tab === "import" && <ImportCenter />}
          {tab === "master" && <MasterList />}
        </div>
      </div>
    </div>
  );
}

// ─── Import Center ───────────────────────────────────────────────────

function ImportCenter() {
  const [uploading, setUploading] = useState(false);
  const [staging, setStaging] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx"].includes(ext)) {
      toast.error("Invalid file type", { description: "Upload a .csv or .xlsx file" });
      return;
    }
    setUploading(true);
    setStaging(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/wedding-tracker/upload`, formData, { withCredentials: true });
      setStaging(data);
      toast.success(`Parsed ${data.total_parsed} rows from ${data.venue_name}`, { description: `${data.skipped_count} skipped` });
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleCommit = async () => {
    if (!staging?.staging_id) return;
    setCommitting(true);
    try {
      const { data } = await axios.post(`${API}/wedding-tracker/commit/${staging.staging_id}`, {}, { withCredentials: true });
      toast.success("Committed to Master List", {
        description: `${data.committed.new} new, ${data.committed.updated} updated, ${data.committed.dropped} dropped`,
      });
      setStaging(null);
    } catch (err) {
      toast.error("Commit failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Upload zone */}
      {!staging && (
        <div
          className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${dragActive ? "border-[#F5D300] bg-[#F5D300]/5" : "border-slate-300 hover:border-slate-400"}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={e => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}
          data-testid="wt-dropzone"
        >
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }} />
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={32} className="animate-spin text-[#0A1F3B]" />
              <p className="text-sm text-slate-600">Parsing file...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload size={32} className="text-slate-400" />
              <p className="text-sm text-[#0A1F3B] font-semibold">Drop your venue report here or click to browse</p>
              <p className="text-xs text-slate-500">Accepts .csv and .xlsx files from Salesforce/Wedgewood exports</p>
            </div>
          )}
        </div>
      )}

      {/* Staging review */}
      {staging && (
        <div className="space-y-4" data-testid="wt-staging">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="font-heading text-base font-bold text-[#0A1F3B]">Import Preview</h3>
              <Badge className="bg-blue-100 text-blue-800 text-xs">{staging.venue_name}</Badge>
            </div>
            <div className="flex gap-4 flex-wrap mb-4">
              <SummaryBadge label="New Weddings" count={staging.summary.new_count} color="green" />
              <SummaryBadge label="Updated" count={staging.summary.updated_count} color="amber" />
              <SummaryBadge label="Dropped" count={staging.summary.dropped_count} color="red" />
              <SummaryBadge label="Unchanged" count={staging.summary.unchanged_count} color="slate" />
              {staging.skipped_count > 0 && (
                <SummaryBadge label="Skipped (invalid)" count={staging.skipped_count} color="orange" />
              )}
            </div>

            {/* New items */}
            {staging.new?.length > 0 && (
              <StagingSection title="New Weddings" items={staging.new} color="green" />
            )}
            {staging.updated?.length > 0 && (
              <StagingSection title="Updated" items={staging.updated} color="amber" showDiffs />
            )}
            {staging.dropped?.length > 0 && (
              <StagingSection title="Dropped" items={staging.dropped} color="red" />
            )}
          </div>

          <div className="flex gap-3">
            <Button onClick={handleCommit} disabled={committing} className="bg-[#0A1F3B] text-white gap-2" data-testid="wt-commit-btn">
              {committing ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {committing ? "Committing..." : "Confirm & Commit to Master List"}
            </Button>
            <Button variant="outline" onClick={() => setStaging(null)} className="border-slate-300 text-[#0A1F3B]" data-testid="wt-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!staging && !uploading && (
        <div className="text-center py-6">
          <p className="text-xs text-slate-500">
            Upload a Salesforce/Wedgewood venue report (.csv or .xlsx). The file should contain a header row starting with
            &quot;Quote Line : Quote : Date of Event&quot;.
          </p>
        </div>
      )}
    </div>
  );
}

function SummaryBadge({ label, count, color }) {
  const colors = {
    green: "bg-green-50 text-green-700 border-green-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    slate: "bg-slate-50 text-slate-600 border-slate-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
  };
  return (
    <div className={`border rounded-lg px-3 py-2 text-center ${colors[color] || colors.slate}`}>
      <div className="text-xl font-bold">{count}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider">{label}</div>
    </div>
  );
}

function StagingSection({ title, items, color, showDiffs }) {
  const [expanded, setExpanded] = useState(items.length <= 10);
  const borderColor = { green: "border-green-200", amber: "border-amber-200", red: "border-red-200" }[color] || "border-slate-200";
  const displayItems = expanded ? items : items.slice(0, 5);

  return (
    <div className={`border ${borderColor} rounded-lg mb-3 overflow-hidden`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-xs font-semibold text-[#0A1F3B]">
        <span>{title} ({items.length})</span>
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500 border-b">
              <th className="text-left px-3 py-1.5">Booking ID</th>
              <th className="text-left px-3 py-1.5">Date</th>
              <th className="text-left px-3 py-1.5">Client</th>
              <th className="text-right px-3 py-1.5">Guests</th>
              {showDiffs && <th className="text-left px-3 py-1.5">Changes</th>}
            </tr>
          </thead>
          <tbody>
            {displayItems.map((item, i) => (
              <tr key={i} className="border-b border-slate-100 text-[#0A1F3B]">
                <td className="px-3 py-1.5 font-mono">{item.booking_id}</td>
                <td className="px-3 py-1.5">{item.event_date}</td>
                <td className="px-3 py-1.5">{item.client_name}</td>
                <td className="px-3 py-1.5 text-right">{item.quantity}</td>
                {showDiffs && (
                  <td className="px-3 py-1.5">
                    {item.diffs?.map((d, j) => (
                      <span key={j} className="inline-block mr-2 text-[10px]">
                        <span className="text-slate-500">{d.field}:</span>{" "}
                        <span className="line-through text-red-500">{d.old_value}</span>{" → "}
                        <span className="text-green-700 font-semibold">{d.new_value}</span>
                      </span>
                    ))}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!expanded && items.length > 5 && (
        <button onClick={() => setExpanded(true)} className="w-full text-center text-xs text-[#0A1F3B] font-semibold py-1.5 hover:bg-slate-50">
          Show all {items.length} items
        </button>
      )}
    </div>
  );
}

// ─── Master List ─────────────────────────────────────────────────────

function MasterList() {
  const [weddings, setWeddings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [filters, setFilters] = useState({ venue_id: "", status: "", start_date: "", end_date: "", manual_only: "" });
  const [selected, setSelected] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await axios.get(`${API}/wedding-tracker/weddings?${params}`, { withCredentials: true });
      setWeddings(data.weddings || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("Failed to load weddings");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/wedding-tracker/venues`, { withCredentials: true }).then(({ data }) => {
      setVenues(data.venues || []);
    }).catch(() => {});
  }, []);

  const toggleCheck = (id) => {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (checked.size === weddings.length) {
      setChecked(new Set());
    } else {
      setChecked(new Set(weddings.map(w => w.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (checked.size === 0) return;
    const confirmed = window.confirm(`Delete ${checked.size} wedding${checked.size > 1 ? "s" : ""}? This cannot be undone.`);
    if (!confirmed) return;
    setDeleting(true);
    try {
      const { data } = await axios.post(`${API}/wedding-tracker/weddings/delete`, { ids: [...checked] }, { withCredentials: true });
      toast.success(`Deleted ${data.deleted_count} wedding${data.deleted_count > 1 ? "s" : ""}`);
      setChecked(new Set());
      load();
    } catch (err) {
      toast.error("Delete failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-lg p-3" data-testid="wt-filters">
        <Input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))}
          className="h-8 text-xs w-36 text-[#0A1F3B]" placeholder="Start" />
        <span className="text-xs text-slate-500">to</span>
        <Input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))}
          className="h-8 text-xs w-36 text-[#0A1F3B]" />
        <select value={filters.venue_id} onChange={e => setFilters(f => ({ ...f, venue_id: e.target.value, manual_only: "" }))}
          className="h-8 text-xs border border-slate-200 rounded px-2 bg-white text-[#0A1F3B]">
          <option value="">All Venues</option>
          {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          <option value="__manual">Manual Orders</option>
        </select>
        <div className="flex gap-1">
          {["", "New", "Updated", "Dropped", "Active", "Completed"].map(s => (
            <button key={s} onClick={() => setFilters(f => ({ ...f, status: s }))}
              className={`px-2 py-1 rounded text-[10px] font-semibold transition-colors ${filters.status === s ? "bg-[#0A1F3B] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          {checked.size > 0 && (
            <Button size="sm" onClick={handleBulkDelete} disabled={deleting}
              className="h-8 text-xs gap-1 bg-red-600 text-white hover:bg-red-700" data-testid="wt-delete-btn">
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Delete {checked.size}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={load} className="h-8 text-xs gap-1 border-slate-300 text-[#0A1F3B]">
            <RefreshCw size={12} /> Refresh
          </Button>
          <Button size="sm" onClick={() => setAddModalOpen(true)} className="h-8 text-xs gap-1 bg-[#0A1F3B] text-white">
            <Plus size={12} /> Add Custom
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-500">
        {checked.size > 0 ? <span className="text-[#0A1F3B] font-semibold">{checked.size} selected · </span> : ""}{total} weddings
      </p>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#0A1F3B]" size={24} /></div>
      ) : weddings.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <FileSpreadsheet size={32} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm">No weddings yet — upload your first venue report in the Import Center.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-xs" data-testid="wt-table">
            <thead>
              <tr className="bg-slate-50 text-slate-500 border-b text-left">
                <th className="px-2 py-2 w-8">
                  <input type="checkbox" checked={weddings.length > 0 && checked.size === weddings.length}
                    onChange={toggleAll} className="rounded border-slate-300 accent-[#0A1F3B]" data-testid="wt-select-all" />
                </th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Venue</th>
                <th className="px-3 py-2">Booking ID</th>
                <th className="px-3 py-2 text-right">Guests</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {weddings.map((w) => (
                <tr key={w.id}
                  className={`border-b border-slate-50 cursor-pointer hover:bg-slate-50/50 transition-colors ${STATUS_STYLES[w.status] || ""} ${checked.has(w.id) ? "ring-1 ring-inset ring-[#0A1F3B]/20 bg-blue-50/30" : ""}`}>
                  <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={checked.has(w.id)}
                      onChange={() => toggleCheck(w.id)} className="rounded border-slate-300 accent-[#0A1F3B]" data-testid={`wt-check-${w.id}`} />
                  </td>
                  <td className="px-3 py-2 font-mono whitespace-nowrap" onClick={() => setSelected(w.id)}>{w.event_date}</td>
                  <td className="px-3 py-2 font-medium text-[#0A1F3B] max-w-[250px] truncate" onClick={() => setSelected(w.id)}>{w.client_name}</td>
                  <td className="px-3 py-2 text-slate-500" onClick={() => setSelected(w.id)}>{w.venue_name}</td>
                  <td className="px-3 py-2 font-mono text-slate-500" onClick={() => setSelected(w.id)}>{w.booking_id}</td>
                  <td className="px-3 py-2 text-right font-mono" onClick={() => setSelected(w.id)}>{w.quantity || "—"}</td>
                  <td className="px-3 py-2" onClick={() => setSelected(w.id)}>
                    <Badge className={`text-[10px] ${STATUS_BADGE[w.status] || ""}`}>{w.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail drawer */}
      {selected && <DetailDrawer weddingId={selected} onClose={() => { setSelected(null); load(); }} venues={venues} />}

      {/* Add modal */}
      {addModalOpen && <AddWeddingModal onClose={() => { setAddModalOpen(false); load(); }} venues={venues} />}
    </div>
  );
}

// ─── Detail Drawer ───────────────────────────────────────────────────

function DetailDrawer({ weddingId, onClose, venues }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/wedding-tracker/weddings/${weddingId}`, { withCredentials: true }).then(({ data }) => {
      setData(data);
      setForm({ client_name: data.client_name, event_date: data.event_date, quantity: data.quantity, secondary_contact_name: data.secondary_contact_name || "", status: data.status });
    }).catch(() => toast.error("Failed to load wedding details")).finally(() => setLoading(false));
  }, [weddingId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/wedding-tracker/weddings/${weddingId}`, form, { withCredentials: true });
      toast.success("Wedding updated");
      setEditing(false);
      // Reload
      const { data: updated } = await axios.get(`${API}/wedding-tracker/weddings/${weddingId}`, { withCredentials: true });
      setData(updated);
    } catch (err) {
      toast.error("Update failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-60 overflow-y-auto border-l border-slate-200" data-testid="wt-detail-drawer">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
        <h3 className="font-semibold text-[#0A1F3B] text-sm">Wedding Details</h3>
        <div className="flex gap-2">
          {!editing && <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="h-7 text-xs gap-1 border-slate-300 text-[#0A1F3B]"><Edit3 size={12} /> Edit</Button>}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={20} /></div>
      ) : data ? (
        <div className="p-5 space-y-5">
          {editing ? (
            <div className="space-y-3">
              <Field label="Client Name" value={form.client_name} onChange={v => setForm(f => ({ ...f, client_name: v }))} />
              <Field label="Event Date" value={form.event_date} onChange={v => setForm(f => ({ ...f, event_date: v }))} type="date" />
              <Field label="Guests" value={form.quantity} onChange={v => setForm(f => ({ ...f, quantity: v }))} type="number" />
              <Field label="Secondary Contact" value={form.secondary_contact_name} onChange={v => setForm(f => ({ ...f, secondary_contact_name: v }))} />
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Status</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  className="h-8 text-xs border border-slate-200 rounded px-2 bg-white w-full text-[#0A1F3B]">
                  {["Active", "New", "Updated", "Dropped", "Completed"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saving} className="bg-[#0A1F3B] text-white text-xs gap-1">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)} className="text-xs border-slate-300 text-[#0A1F3B]">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <InfoRow label="Booking ID" value={data.booking_id} mono />
              <InfoRow label="Quote ID" value={data.quote_id} mono />
              <InfoRow label="Client" value={data.client_name} />
              <InfoRow label="Event Date" value={data.event_date} />
              <InfoRow label="Guests" value={data.quantity || "—"} />
              <InfoRow label="Venue" value={data.venue_name} />
              <InfoRow label="Secondary Contact" value={data.secondary_contact_name || "—"} />
              <InfoRow label="Status" value={<Badge className={STATUS_BADGE[data.status]}>{data.status}</Badge>} />
              {data.is_manually_added && <InfoRow label="Source" value="Manually Added" />}
            </div>
          )}

          {/* Audit Timeline */}
          {data.audit_logs && data.audit_logs.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
                <Clock size={12} /> Change History
              </h4>
              <div className="space-y-1.5">
                {data.audit_logs.map((log, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-100 rounded px-3 py-1.5 text-[11px]">
                    <span className="text-slate-500">{log.field_changed}:</span>{" "}
                    <span className="line-through text-red-500">{log.old_value}</span>{" → "}
                    <span className="text-green-700 font-semibold">{log.new_value}</span>
                    <div className="text-[9px] text-slate-400 mt-0.5">{log.changed_at ? new Date(log.changed_at).toLocaleString() : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {data.audit_logs?.length === 0 && (
            <p className="text-xs text-slate-400 italic">No changes recorded yet.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm text-[#0A1F3B] ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      <Input type={type} value={value} onChange={e => onChange(e.target.value)} className="h-8 text-xs text-[#0A1F3B]" />
    </div>
  );
}

// ─── Add Wedding Modal ───────────────────────────────────────────────

function AddWeddingModal({ onClose, venues }) {
  const [form, setForm] = useState({ client_name: "", event_date: "", quantity: "", secondary_contact_name: "", venue_id: "" });
  const [saving, setSaving] = useState(false);

  const manualVenue = venues.find(v => v.name === "Manual Orders");

  const handleSave = async () => {
    if (!form.client_name || !form.event_date) {
      toast.error("Client name and event date are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/wedding-tracker/weddings`, {
        ...form,
        venue_id: form.venue_id || (manualVenue?.id || ""),
        quantity: parseInt(form.quantity) || 0,
      }, { withCredentials: true });
      toast.success("Wedding added");
      onClose();
    } catch (err) {
      toast.error("Failed to add", { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" data-testid="wt-add-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base font-bold text-[#0A1F3B]">Add Custom Wedding</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Client Name" value={form.client_name} onChange={v => setForm(f => ({ ...f, client_name: v }))} />
          <Field label="Event Date" value={form.event_date} onChange={v => setForm(f => ({ ...f, event_date: v }))} type="date" />
          <Field label="Guest Count" value={form.quantity} onChange={v => setForm(f => ({ ...f, quantity: v }))} type="number" />
          <Field label="Secondary Contact" value={form.secondary_contact_name} onChange={v => setForm(f => ({ ...f, secondary_contact_name: v }))} />
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Venue</label>
            <select value={form.venue_id} onChange={e => setForm(f => ({ ...f, venue_id: e.target.value }))}
              className="h-8 text-xs border border-slate-200 rounded px-2 bg-white w-full text-[#0A1F3B]">
              <option value="">Manual Orders</option>
              {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <Button onClick={handleSave} disabled={saving} className="bg-[#0A1F3B] text-white text-xs gap-1">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add Wedding
          </Button>
          <Button variant="outline" onClick={onClose} className="text-xs border-slate-300 text-[#0A1F3B]">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
